import React, { useState, useEffect } from 'react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Eye, Lock, Gavel, BarChart3, Info, Users } from 'lucide-react';
import { TYPE_LABELS } from './constants';
import { useExpenditureLabels } from '../../contexts/ExpenditureLabelsContext';
import TypeAheadLookup from '../../components/TypeAheadLookup';
import api from '../../services/api';

const CURRENCIES = ['USD','GBP','EUR','CAD','AUD','CHF','JPY','CNY','INR','SGD','AED','SAR','KRW','BRL','MXN','ZAR','NZD','SEK','NOK','DKK'];

const AUCTION_CONFIG_TOOLTIP = `Bidding Structure: Determines how suppliers submit their bids. Single Item = one price for one item. Multi-Line = price per line item. Lot-Based = one price per grouped lot. Total Basket = one grand total for everything.

Award Logic: How the winning bid is determined. Lowest per Item splits the award to the cheapest supplier per line. Lowest Total awards everything to one supplier with the lowest overall bid. Split Award allows manual cherry-picking.

Bid Visibility: Controls what suppliers see during the auction. Open = suppliers see the current lowest bid. Sealed = all bids hidden until the auction closes.

Min. Decrement: The minimum amount a new bid must be lower than the current best. Prevents trivial undercutting (e.g. bidding $0.01 less).

Auto-extend: When enabled, the auction clock extends if a bid is placed in the final minutes — prevents last-second "sniping".

Extension (mins): How many minutes to add when auto-extend triggers.`;

const EVAL_WEIGHTS_TOOLTIP = `Evaluation Weights determine how suppliers are ranked beyond just price:

Price %: Weight given to the bid amount (typically 60-80%).
Delivery %: Weight for delivery speed, reliability, and logistics capability.
Quality %: Weight for product/service quality, certifications, and track record.
ESG / CO2 %: Weight for environmental, social, and governance factors.

All weights must total 100%. The Baseline Estimate is your expected total cost before the auction — used to calculate savings achieved.`;

export function DetailsTab({ form, updateField, setForm, isNew }) {
  const { labels } = useExpenditureLabels();
  const currency = form.currency || 'USD';
  const [rfxGroups, setRfxGroups] = useState([]);

  useEffect(() => {
    api.get('/api/admin/groups-by-type/rfx').then(r => setRfxGroups(r.data.groups || [])).catch(() => {});
  }, []);

  const fmtCurrency = (val) => {
    const num = parseFloat(val) || 0;
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="space-y-4 py-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label>Title *</Label>
          <Input value={form.title} onChange={e => updateField('title', e.target.value)} placeholder="e.g. Annual IT Hardware Supply 2026"
            data-testid="rfx-title" />
        </div>
        <div>
          <Label>Type</Label>
          <Select value={form.type} onValueChange={v => updateField('type', v)} disabled={!isNew}>
            <SelectTrigger data-testid="rfx-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(TYPE_LABELS).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Status</Label>
          <Input value={form.status || 'DRAFT'} disabled className="bg-slate-50" />
        </div>
        <div>
          <Label>Category</Label>
          <TypeAheadLookup endpoint="/api/data/categories" valueField="code" labelField="description"
            value={form.category || ''} displayValue={form.category || ''}
            placeholder="Search categories..." onChange={code => updateField('category', code)}
            data-testid="rfx-category" />
        </div>
        <div>
          <Label>Currency *</Label>
          <Select value={currency} onValueChange={v => updateField('currency', v)}>
            <SelectTrigger data-testid="rfx-currency"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label>Start Date *</Label><Input type="datetime-local" value={form.start_date} onChange={e => updateField('start_date', e.target.value)} data-testid="rfx-start-date" /></div>
        <div><Label>End Date *</Label><Input type="datetime-local" value={form.end_date} onChange={e => updateField('end_date', e.target.value)} data-testid="rfx-end-date" /></div>
        <div><Label>Closing Date (Deadline for Responses) *</Label><Input type="datetime-local" value={form.closing_date} onChange={e => updateField('closing_date', e.target.value)} data-testid="rfx-closing-date" /></div>
      </div>

      {/* Expense Codes */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label>{labels.exp_code_1 || 'Expense Code 1'}</Label>
          <TypeAheadLookup endpoint="/api/account-codes/1" valueField="code" labelField="description"
            value={form.expense_code_1 || ''} displayValue={form.expense_code_1 || ''}
            placeholder={`Search ${labels.exp_code_1 || 'Expense Code 1'}...`}
            onChange={code => updateField('expense_code_1', code)}
            data-testid="rfx-expense-code-1" />
        </div>
        <div>
          <Label>{labels.exp_code_2 || 'Expense Code 2'}</Label>
          <TypeAheadLookup endpoint="/api/account-codes/2" valueField="code" labelField="description"
            value={form.expense_code_2 || ''} displayValue={form.expense_code_2 || ''}
            placeholder={`Search ${labels.exp_code_2 || 'Expense Code 2'}...`}
            onChange={code => updateField('expense_code_2', code)}
            data-testid="rfx-expense-code-2" />
        </div>
        <div>
          <Label>{labels.exp_code_3 || 'Expense Code 3'}</Label>
          <TypeAheadLookup endpoint="/api/account-codes/3" valueField="code" labelField="description"
            value={form.expense_code_3 || ''} displayValue={form.expense_code_3 || ''}
            placeholder={`Search ${labels.exp_code_3 || 'Expense Code 3'}...`}
            onChange={code => updateField('expense_code_3', code)}
            data-testid="rfx-expense-code-3" />
        </div>
      </div>

      {/* RFx Management Group */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <Label className="flex items-center gap-1"><Users className="w-3.5 h-3.5 text-slate-500" /> RFx Management Group</Label>
            <div className="relative group">
              <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" />
              <div className="absolute left-0 top-full mt-1 z-50 w-72 p-3 bg-slate-900 text-white text-xs rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-colors pointer-events-none leading-relaxed">
                The RFx Management Group is responsible for this sourcing event. Members receive email notifications when suppliers ask questions and are responsible for answering them. All published Q&A is visible to all participating suppliers.
              </div>
            </div>
          </div>
          <Select value={form.rfx_management_group || '__none__'} onValueChange={v => updateField('rfx_management_group', v === '__none__' ? '' : v)}>
            <SelectTrigger data-testid="rfx-management-group"><SelectValue placeholder="Select management group..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No group assigned</SelectItem>
              {rfxGroups.map(g => <SelectItem key={g.group_code} value={g.group_code}>{g.group_code} — {g.description || ''}</SelectItem>)}
            </SelectContent>
          </Select>
          {rfxGroups.length === 0 && <p className="text-[10px] text-amber-600 mt-1">No RFx groups defined yet. Create one in System Admin → Group Approval.</p>}
        </div>
      </div>

      <div><Label>Description *</Label><Textarea value={form.description} onChange={e => updateField('description', e.target.value)}
        rows={4} placeholder="Describe the requirements, scope, and expectations..." data-testid="rfx-description" /></div>

      <div className="border rounded-lg p-4 bg-slate-50/50">
        <div className="flex items-center gap-2">
          <Checkbox checked={form.public_listing !== false} onCheckedChange={c => updateField('public_listing', c)}
            data-testid="public-listing-toggle" />
          <Label className="font-medium flex items-center gap-1.5"><Eye className="w-4 h-4 text-slate-600" /> List on Public Sourcing Events Page</Label>
        </div>
        <p className="text-xs text-slate-500 ml-6 mt-1">When enabled, this event will appear on the public-facing sourcing events page where any supplier can discover it. Disable this if the event should only be visible to specifically invited suppliers.</p>
      </div>

      <div className="border rounded-lg p-4 bg-amber-50/50">
        <div className="flex items-center gap-2 mb-2">
          <Checkbox checked={form.sealed_bidding?.enabled} onCheckedChange={c => setForm(f => ({ ...f, sealed_bidding: { ...f.sealed_bidding, enabled: c } }))}
            data-testid="sealed-bidding-toggle" />
          <Label className="font-medium flex items-center gap-1.5"><Lock className="w-4 h-4 text-amber-600" /> Sealed Bidding</Label>
        </div>
        <p className="text-xs text-slate-500 ml-6 mb-2">When enabled, supplier responses are hidden until the reveal date. Required for government RFP bids.</p>
        {form.sealed_bidding?.enabled && (
          <div className="ml-6">
            <Label>Reveal Date & Time</Label>
            <Input type="datetime-local" value={form.sealed_bidding.reveal_date || ''}
              onChange={e => setForm(f => ({ ...f, sealed_bidding: { ...f.sealed_bidding, reveal_date: e.target.value } }))}
              data-testid="sealed-reveal-date" />
          </div>
        )}
      </div>

      {/* Savings Baseline Configuration */}
      <div className="border rounded-lg p-4 bg-emerald-50/50">
        <div className="flex items-center gap-2 mb-2">
          <BarChart3 className="w-4 h-4 text-emerald-600" />
          <h3 className="font-medium text-sm">Savings Baseline</h3>
          <div className="relative group">
            <Info className="w-4 h-4 text-slate-400 cursor-help" />
            <div className="absolute left-0 top-full mt-1 z-50 w-80 p-3 bg-slate-900 text-white text-xs rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-colors duration-200 whitespace-pre-line leading-relaxed pointer-events-none">
{`The baseline method determines how savings are calculated for this event.

Last Purchase Price: Compares awarded prices against the last price paid for equivalent items.
Average Price: Uses the average of all bids received as the baseline.
Budget Price: Uses the estimated/budget price set on line items.
First Bid: Uses the first bid submitted as the baseline (useful for auctions).
Manual: Allows you to enter a specific baseline price per item.

Your organisation's default is pre-selected. Override if this event needs a different approach.`}
            </div>
          </div>
        </div>
        <p className="text-xs text-slate-500 mb-3">Choose how savings will be measured for this event. This determines the reference price used to calculate procurement savings.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Baseline Method</Label>
            <Select value={form.savings_baseline?.method || ''} onValueChange={v => setForm(f => ({ ...f, savings_baseline: { ...(f.savings_baseline || {}), method: v } }))}>
              <SelectTrigger className="h-8 text-xs" data-testid="baseline-method"><SelectValue placeholder="Use organisation default" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="last_purchase_price">Last Purchase Price</SelectItem>
                <SelectItem value="average_price">Average Price</SelectItem>
                <SelectItem value="budget_price">Budget / Estimated Price</SelectItem>
                <SelectItem value="first_bid">First Bid Received</SelectItem>
                <SelectItem value="manual">Manual Entry</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Override Justification</Label>
            <Input value={form.savings_baseline?.justification || ''} placeholder="Why a different baseline method is needed for this event..."
              onChange={e => setForm(f => ({ ...f, savings_baseline: { ...(f.savings_baseline || {}), justification: e.target.value } }))}
              className="h-8 text-xs" data-testid="baseline-justification" />
          </div>
        </div>
      </div>

      {form.type === 'REVERSE_AUCTION' && (
        <div className="border rounded-lg p-4 bg-rose-50/50 space-y-4">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-sm flex items-center gap-1.5"><Gavel className="w-4 h-4 text-rose-600" /> Auction Configuration</h3>
            <div className="relative group">
              <Info className="w-4 h-4 text-slate-400 cursor-help" />
              <div className="absolute left-0 top-full mt-1 z-50 w-96 p-3 bg-slate-900 text-white text-xs rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-colors duration-200 whitespace-pre-line leading-relaxed pointer-events-none">
                {AUCTION_CONFIG_TOOLTIP}
              </div>
            </div>
            <span className="text-xs text-slate-400 ml-auto font-medium">{currency}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Bidding Structure *</Label>
              <Select value={form.bidding_structure || 'multi_line'} onValueChange={v => updateField('bidding_structure', v)}>
                <SelectTrigger data-testid="bidding-structure"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single_item">Single Item</SelectItem>
                  <SelectItem value="multi_line">Multi-Line Item</SelectItem>
                  <SelectItem value="lot_based">Lot-Based</SelectItem>
                  <SelectItem value="total_basket">Total Basket</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-500 mt-1">
                {form.bidding_structure === 'single_item' && 'Suppliers bid one price on a single item'}
                {form.bidding_structure === 'multi_line' && 'Suppliers bid per line item (can bid on all or some)'}
                {form.bidding_structure === 'lot_based' && 'Items grouped into lots — suppliers bid per lot'}
                {(!form.bidding_structure || form.bidding_structure === 'total_basket') && 'Suppliers bid one grand total for all items'}
              </p>
            </div>
            <div>
              <Label>Award Logic</Label>
              <Select value={form.award_logic || 'lowest_per_item'} onValueChange={v => updateField('award_logic', v)}>
                <SelectTrigger data-testid="award-logic"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lowest_per_item">Lowest per Item</SelectItem>
                  <SelectItem value="lowest_per_lot">Lowest per Lot</SelectItem>
                  <SelectItem value="lowest_total">Lowest Total</SelectItem>
                  <SelectItem value="split_award">Split Award</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Bid Visibility</Label>
              <Select value={form.bid_visibility || 'open'} onValueChange={v => updateField('bid_visibility', v)}>
                <SelectTrigger data-testid="bid-visibility"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open (suppliers see current lowest)</SelectItem>
                  <SelectItem value="sealed">Sealed (bids hidden until close)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><Label>Min. Decrement ({currency})</Label><Input type="number" value={form.auction_settings?.minimum_decrement || ''}
              onChange={e => setForm(f => ({ ...f, auction_settings: { ...f.auction_settings, minimum_decrement: e.target.value } }))}
              placeholder="e.g. 50" data-testid="auction-min-decrement" /></div>
            <div className="flex items-end gap-2">
              <Checkbox checked={form.auction_settings?.auto_extend}
                onCheckedChange={c => setForm(f => ({ ...f, auction_settings: { ...f.auction_settings, auto_extend: c } }))} />
              <Label>Auto-extend on late bids</Label>
            </div>
            <div><Label>Extension (mins)</Label><Input type="number" value={form.auction_settings?.extension_minutes || 5}
              onChange={e => setForm(f => ({ ...f, auction_settings: { ...f.auction_settings, extension_minutes: parseInt(e.target.value) || 5 } }))} /></div>
          </div>
        </div>
      )}

      {form.type === 'REVERSE_AUCTION' && (
        <div className="border rounded-lg p-4 bg-indigo-50/50">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-medium text-sm flex items-center gap-1.5"><BarChart3 className="w-4 h-4 text-indigo-600" /> Evaluation Weights</h3>
            <div className="relative group">
              <Info className="w-4 h-4 text-slate-400 cursor-help" />
              <div className="absolute left-0 top-full mt-1 z-50 w-80 p-3 bg-slate-900 text-white text-xs rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-colors duration-200 whitespace-pre-line leading-relaxed pointer-events-none">
                {EVAL_WEIGHTS_TOOLTIP}
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500 mb-3">Set how bids are weighted. Leave blank to use system defaults. Must total 100%.</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { key: 'price', label: 'Price %', def: 70 },
              { key: 'delivery', label: 'Delivery %', def: 15 },
              { key: 'quality', label: 'Quality %', def: 10 },
              { key: 'esg', label: 'ESG / CO2 %', def: 5 },
            ].map(w => (
              <div key={w.key}>
                <Label className="text-xs">{w.label}</Label>
                <Input type="number" value={form.evaluation_weights?.[w.key] ?? ''} placeholder={String(w.def)}
                  onChange={e => setForm(f => ({ ...f, evaluation_weights: { ...(f.evaluation_weights || {}), [w.key]: parseFloat(e.target.value) || 0 } }))}
                  className="h-8 text-xs" />
              </div>
            ))}
            <div>
              <Label className="text-xs">Baseline Est. ({currency})</Label>
              <div className="relative">
                <Input type="number" value={form.baseline_estimate || ''} placeholder="0.00"
                  onChange={e => updateField('baseline_estimate', parseFloat(e.target.value) || 0)}
                  className="h-8 text-xs" data-testid="baseline-estimate" />
              </div>
              {form.baseline_estimate > 0 && (
                <p className="text-[10px] text-indigo-600 mt-0.5 font-mono">{currency} {fmtCurrency(form.baseline_estimate)}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
